/**
 * Cache discipline for save/unsave (SPEC `saved-posts` § Cache discipline).
 *
 * A save must **not** invalidate the feed, the posts table or the notification list: those are
 * ordered, cursor-paginated surfaces, and refetching them would reshuffle the list under a reader
 * who did nothing but bookmark a post — the one thing a feed must never do. So the bookmark state
 * is *patched in place* across every cached page instead.
 *
 * The patch is structural rather than per-endpoint on purpose: `saved` rides `FeedCardDTO`, so it
 * appears inside #26, #48, #51 and #66 pages, inside a notification's `card`, and inside the post
 * detail — six shapes today and more later. Rather than teach this module about each of them, it
 * walks the cached value and updates any object that *is* one of the two known carriers:
 *
 *  - a card/detail: `{id: <postId>, saved: boolean, savedInfo}`
 *  - a table row:   `{id: <postId>, savedAt, savedAtDisplay}`
 *
 * Both display strings come from the server's #64 response — nothing is formatted here (§12.5).
 * Branches that change are rebuilt; everything else keeps its identity, so React Query's structural
 * comparison only re-renders what actually moved.
 */

import type { SavedInfoDTO } from '@lw/shared';
import type { QueryClient } from '@tanstack/react-query';
import { QK } from '../../api/queryKeys';

/** What a save/unsave leaves behind on a post, in the two spellings the DTOs use. */
export type SavedPatch = {
  postId: number;
  saved: boolean;
  /** `null` on unsave; otherwise the server's `savedAt`. */
  savedAt: number | null;
  /** `null` on unsave; otherwise the server's own rendering of it. */
  savedAtDisplay: string | null;
  /**
   * The annotation rows, built server-side by #64 (`null` on unsave). Every string in it is the
   * server's, list names included, so an edit lands on every cached card without a second fetch.
   */
  savedInfo: SavedInfoDTO | null;
};

/** Guards against pathological structures; every cached page is far shallower than this. */
const MAX_DEPTH = 12;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function patchValue(value: unknown, patch: SavedPatch, depth: number): unknown {
  if (depth > MAX_DEPTH || !isRecord(value)) return value;

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const patched = patchValue(entry, patch, depth + 1);
      if (patched !== entry) changed = true;
      return patched;
    });
    return changed ? next : value;
  }

  let result = value;
  const replace = (fields: Record<string, unknown>): void => {
    result = result === value ? { ...value, ...fields } : { ...result, ...fields };
  };

  if (value.id === patch.postId) {
    if (typeof value.saved === 'boolean') {
      // A card or the post detail. Both fields move together: `savedInfo` is only ever set on the
      // two carriers of `saved`, and `SavedStateDTO` has no `id`, so this cannot mis-target.
      if (value.saved !== patch.saved) replace({ saved: patch.saved });
      // Rebuilt whenever either side is non-null: an edit changes the block's contents without
      // changing `saved`, so there is nothing cheaper than the new object to compare against.
      if ('savedInfo' in value && !(value.savedInfo === null && patch.savedInfo === null)) {
        replace({ savedInfo: patch.savedInfo });
      }
    } else if ('savedAt' in value && 'savedAtDisplay' in value) {
      // A `PostRowDTO`. Requiring the pair keeps the match unambiguous: nothing else carries both.
      if (value.savedAt !== patch.savedAt || value.savedAtDisplay !== patch.savedAtDisplay) {
        replace({ savedAt: patch.savedAt, savedAtDisplay: patch.savedAtDisplay });
      }
    }
  }

  for (const [key, entry] of Object.entries(result)) {
    const patched = patchValue(entry, patch, depth + 1);
    if (patched !== entry) replace({ [key]: patched });
  }

  return result;
}

/**
 * The query roots whose payloads can contain a post DTO. Scoped rather than "every query", so a
 * bookmark never walks the settings, status or profile caches looking for something that cannot be
 * there. A new post-carrying endpoint adds its root here.
 */
const POST_BEARING_ROOTS: readonly (readonly unknown[])[] = [
  QK.views,
  QK.feed,
  QK.saved,
  QK.notifications,
  ['post'],
];

/**
 * Applies the patch to every cached page of those roots. Cheap: the walk only rebuilds the path to
 * a match, so a cache with no page mentioning this post keeps its object identity throughout.
 */
export function patchSavedInCaches(queryClient: QueryClient, patch: SavedPatch): void {
  for (const queryKey of POST_BEARING_ROOTS) {
    queryClient.setQueriesData({ queryKey }, (data: unknown) => patchValue(data, patch, 0));
  }
}
